// DEPENDENCIES
import React, {Component} from "react";
import {connect} from "react-redux";
import _ from "lodash";

// STORE
import {ActionCreator} from "../../../../../../actions/actions";
import {ConfigurationsActionCreators} from "../../../../actions/actionCreators";
import {DenominationsList} from "../../../../reducers/denominations.reducer";
import {
    GROUP_TYPE,
    SpecialPrizeConfiguration,
    TerminalGroupConfiguration,
    GameGroupConfiguration
} from "../../../../reducers/editingConfiguration.reducer";
import {ProgressiveRulesSchema} from "../../../../reducers/schema.reducer";
import {configurationsStoreSelectors} from "../../../../store/store";
import {ConfigurationsActionTypes} from "../../../../actions/actionTypes";

// COMPONENTS
import {Dialog} from "../../../../../../utils/Dialog/Dialog.component";
import FormSet from "../../../../../../utils/forms/components/FormSet";
import FormSetWays from "../../../../../../utils/forms/components/FormSetWays";

// UTILS
import {SelectedGroupsByProgressive} from "../../../../models/SelectedGroupsByProgressive";

// FORM CREATORS
import {
    generateValidation,
    createValidation,
    validateWays
} from "../../../Utils/validation";
import {normalSubforms} from "./FormBuild/FormTypes/Normal";
import {proportionalSubforms} from "./FormBuild/FormTypes/Proportional";
import {fractionatedSubforms} from "./FormBuild/FormTypes/Fractionated";
import {waysSubforms, createPrizesSubForm} from "./FormBuild/FormTypes/Ways";

// CONTANTS AND DEFAULTS
import * as CONFIG_CONSTANTS from "./FormBuild/ProgressivesConfigurationSection.constants";

// LOAD STYLES
import "./ProgressivesConfigurationSection.component.scss";

export interface ProgressivesConfigurationSectionProps {
    progressivesConfig: SpecialPrizeConfiguration[];
    updateProgressivesConfig: ActionCreator;
    configSectionVisited: ActionCreator;
    terminalGroups: TerminalGroupConfiguration[];
    selectedTerminalGroups: SelectedGroupsByProgressive;
    selectedGameGroups: SelectedGroupsByProgressive;
    gameGroups: GameGroupConfiguration[];
    schema: any;
    denominationsList: DenominationsList;
    selectedGroupType: GROUP_TYPE;
    progressiveRules: ProgressiveRulesSchema[];
    editingConfiguration: any;
    gameInstances: any;
    getGameInstances: ActionCreator;
}

export interface ProgressvesConfigurationSectionState {
    progressivesConfig: any[];
    showDialog: boolean;
    indexToDelete: number;
    deleteCallback: any;
}

const defaultState = {
    progressivesConfig: [],
    showDialog: false,
    indexToDelete: null,
    deleteCallback: null
};

class ProgressivesConfigurationSection extends Component<
    ProgressivesConfigurationSectionProps,
    ProgressvesConfigurationSectionState
> {
    fractionatedFormSet: React.RefObject<FormSet>;
    proportionalFormSet: React.RefObject<FormSet>;
    normalFormSet: React.RefObject<FormSet>;
    levelFormSet: React.RefObject<FormSet>;
    multipotsFormSet: React.RefObject<FormSet>;
    jumbolinkFormSet: React.RefObject<FormSet>;
    reelstrikeFormSet: React.RefObject<FormSet>;
    easylinkFormSet: React.RefObject<FormSet>;
    sapFormSet: React.RefObject<FormSet>;
    luckytokenslinkFormSet: React.RefObject<FormSet>;

    private previousFractionatedValue = {values: [], isValid: true};
    private previousProportionalValue = {values: [], isValid: true};
    private previousNormalValue = {values: [], isValid: true};
    private previousLevelValue = {values: [], isValid: true};
    private previousMultipotsValue = {values: [], isValid: true};
    private previousJumbolinkValue = {values: [], isValid: true};
    private previousReelstrikeValue = {values: [], isValid: true};
    private previousEasylinkValue = {values: [], isValid: true};
    private previousSAPValue = {values: [], isValid: true};
    private previousLuckytokenslinkValue = {values: [], isValid: true};
    private prizeValidationSchema = undefined;
    private progressiveValidationSchema = undefined;
    private sapValidationSchema = undefined;
    private sapPrizeValidationSchema = undefined;
    private multipotsValidationSchema = undefined;
    private jumbolinkValidationSchema = undefined;
    private reelstrikeValidationSchema = undefined;
    private luckytokenslinkValidationSchema = undefined;

    constructor(props) {
        super(props);

        this.state = defaultState;
        this.onDialogConfirm = this.onDialogConfirm.bind(this);
        this.onDialogClose = this.onDialogClose.bind(this);

        this.fractionatedFormSet = React.createRef();
        this.proportionalFormSet = React.createRef();
        this.normalFormSet = React.createRef();
        this.levelFormSet = React.createRef();
        this.multipotsFormSet = React.createRef();
        this.jumbolinkFormSet = React.createRef();
        this.reelstrikeFormSet = React.createRef();
        this.easylinkFormSet = React.createRef();
        this.sapFormSet = React.createRef();
        this.luckytokenslinkFormSet = React.createRef();

        this.props.getGameInstances({
            subsidiary: this.props.editingConfiguration.subsidiary.id
        });
    }

    onFractionatedsChange = formEvent => {
        this.previousFractionatedValue = {...formEvent};
        this.updateFromPreviousData();
    };

    onProportionalsChange = formEvent => {
        this.previousProportionalValue = {...formEvent};
        this.updateFromPreviousData();
    };

    onNormalsChange = formEvent => {
        this.previousNormalValue = {...formEvent};
        this.updateFromPreviousData();
    };

    onLevelsChange = formEvent => {
        let properties = {...this.props.schema.definitions.$progressive.properties}
        properties["id"]["unique"] = formEvent.values.map(progressive => progressive.id);
        const validationSchema = generateValidation(
            createValidation(
                this.props.schema.definitions.$progressive.properties,
                this.props.schema.definitions.$progressive.required
            )
        );
        this.previousLevelValue = {...formEvent};
        this.previousLevelValue.isValid = validateWays(
            this.previousLevelValue.values,
            validationSchema,
            this.prizeValidationSchema
        );

        this.updateFromPreviousData();
    };

    onMultipotsChange = formEvent => {
        let properties = {...this.props.schema.definitions.$multipots.properties}
        properties["id"]["unique"] = formEvent.values.map(progressive => progressive.id);
        this.multipotsValidationSchema = generateValidation(
            createValidation(
                properties,
                this.props.schema.definitions.$multipots.required
            )
        );
        this.previousMultipotsValue = {...formEvent};
        this.previousMultipotsValue.isValid = validateWays(
            this.previousMultipotsValue.values,
            this.multipotsValidationSchema,
            this.prizeValidationSchema
        );
        this.updateFromPreviousData();
    };

    onJumbolinkChange = formEvent => {
        let properties = {...this.props.schema.definitions.$jumbolink.properties}
        properties["id"]["unique"] = formEvent.values.map(progressive => progressive.id);
        this.jumbolinkValidationSchema = generateValidation(
            createValidation(
                properties,
                this.props.schema.definitions.$jumbolink.required
            )
        );
        this.previousJumbolinkValue = {...formEvent};
        this.previousJumbolinkValue.isValid = validateWays(
            this.previousJumbolinkValue.values,
            this.jumbolinkValidationSchema,
            this.prizeValidationSchema
        );
        this.updateFromPreviousData();
    };

    onReelstrikeChange = formEvent => {
        let properties = {...this.props.schema.definitions.$reelstrike.properties}
        properties["id"]["unique"] = formEvent.values.map(progressive => progressive.id);
        this.reelstrikeValidationSchema = generateValidation(
            createValidation(
                properties,
                this.props.schema.definitions.$reelstrike.required
            )
        );
        this.previousReelstrikeValue = {...formEvent};
        let _prizeValidationSchema = generateValidation(
            createValidation(
                this.props.schema.definitions.$reelstrike.properties.prizes
                    .items.properties,
                this.props.schema.definitions.$reelstrike.properties.prizes
                    .items.required
            )
        );
        this.previousReelstrikeValue.isValid = validateWays(
            this.previousReelstrikeValue.values,
            this.reelstrikeValidationSchema,
            _prizeValidationSchema
        );
        this.updateFromPreviousData();
    };

    onEasylinkChange = formEvent => {
        let properties = {...this.props.schema.definitions.$progressive.properties}
        properties["id"]["unique"] = formEvent.values.map(progressive => progressive.id);
        const validationSchema = generateValidation(
            createValidation(
                this.props.schema.definitions.$progressive.properties,
                this.props.schema.definitions.$progressive.required
            )
        );
        this.previousEasylinkValue = {...formEvent};
        this.previousEasylinkValue.isValid = validateWays(
            this.previousEasylinkValue.values,
            validationSchema,
            this.prizeValidationSchema
        );
        this.updateFromPreviousData();
    };

    onSAPChange = formEvent => {
        let properties = {...this.props.schema.definitions.$sap_jackpot.properties}
        properties["id"]["unique"] = formEvent.values.map(progressive => progressive.id);
        this.sapValidationSchema = generateValidation(
            createValidation(
                this.props.schema.definitions.$sap_jackpot.properties,
                this.props.schema.definitions.$sap_jackpot.required
            )
        );
        this.previousSAPValue = {...formEvent};
        this.previousSAPValue.isValid = validateWays(
            this.previousSAPValue.values,
            this.sapValidationSchema,
            this.sapPrizeValidationSchema
        );
        this.updateFromPreviousData();
    };

    onLuckytokenslinkChange = formEvent => {
        let properties = {...this.props.schema.definitions.$luckytokenslink.properties}
        properties["id"]["unique"] = formEvent.values.map(progressive => progressive.id);
        this.luckytokenslinkValidationSchema = generateValidation(
            createValidation(
                properties,
                this.props.schema.definitions.$luckytokenslink.required
            )
        );
        this.previousLuckytokenslinkValue = {...formEvent};
        this.previousLuckytokenslinkValue.isValid = validateWays(
            this.previousLuckytokenslinkValue.values,
            this.luckytokenslinkValidationSchema,
            this.prizeValidationSchema
        );
        this.updateFromPreviousData();
    };

    updateFromPreviousData = () => {
        this.props.updateProgressivesConfig({
            config: [
                ...this.previousFractionatedValue.values,
                ...this.previousProportionalValue.values,
                ...this.previousNormalValue.values,
                ...this.previousLevelValue.values,
                ...this.previousMultipotsValue.values,
                ...this.previousJumbolinkValue.values,
                ...this.previousReelstrikeValue.values,
                ...this.previousEasylinkValue.values,
                ...this.previousSAPValue.values,
                ...this.previousLuckytokenslinkValue.values
            ],
            isValid:
                this.previousFractionatedValue.isValid &&
                this.previousProportionalValue.isValid &&
                this.previousNormalValue.isValid &&
                this.previousLevelValue.isValid &&
                this.previousMultipotsValue.isValid &&
                this.previousJumbolinkValue.isValid &&
                this.previousReelstrikeValue.isValid &&
                this.previousEasylinkValue.isValid &&
                this.previousSAPValue.isValid &&
                this.previousLuckytokenslinkValue.isValid &&
                [
                    ...this.previousFractionatedValue.values,
                    ...this.previousProportionalValue.values,
                    ...this.previousNormalValue.values,
                    ...this.previousLevelValue.values,
                    ...this.previousMultipotsValue.values,
                    ...this.previousJumbolinkValue.values,
                    ...this.previousReelstrikeValue.values,
                    ...this.previousEasylinkValue.values,
                    ...this.previousSAPValue.values,
                    ...this.previousLuckytokenslinkValue.values
                ].length > 0,
            groupType: this.props.selectedGroupType
        });
    };

    componentDidMount() {
        this.props.configSectionVisited(
            ConfigurationsActionTypes.UPDATE_PROGRESSIVES_CONFIG
        );

        this.prizeValidationSchema = generateValidation(
            createValidation(
                this.props.schema.definitions.$progressive.properties.prizes
                    .items.properties,
                this.props.schema.definitions.$progressive.properties.prizes
                    .items.required
            )
        );
        if (this.props.schema.definitions.hasOwnProperty("$sap_jackpot")) {
            this.sapPrizeValidationSchema = generateValidation(
                createValidation(
                    this.props.schema.definitions.$sap_jackpot.properties.prizes
                        .items.properties,
                    this.props.schema.definitions.$sap_jackpot.properties.prizes
                        .items.required
                )
            );

            this.sapValidationSchema = generateValidation(
                createValidation(
                    this.props.schema.definitions.$sap_jackpot.properties,
                    this.props.schema.definitions.$sap_jackpot.required
                )
            );
        }
        this.progressiveValidationSchema = generateValidation(
            createValidation(
                this.props.schema.definitions.$progressive.properties,
                this.props.schema.definitions.$progressive.required
            )
        );

        this.multipotsValidationSchema = generateValidation(
            createValidation(
                this.props.schema.definitions.$multipots.properties,
                this.props.schema.definitions.$multipots.required
            )
        );
        if (this.props.schema.definitions.hasOwnProperty("$jumbolink")) {
            this.jumbolinkValidationSchema = generateValidation(
                createValidation(
                    this.props.schema.definitions.$jumbolink.properties,
                    this.props.schema.definitions.$jumbolink.required
                )
            );
        }
        if (this.props.schema.definitions.hasOwnProperty("$reelstrike")) {
            this.reelstrikeValidationSchema = generateValidation(
                createValidation(
                    this.props.schema.definitions.$reelstrike.properties,
                    this.props.schema.definitions.$reelstrike.required
                )
            );
        }
        if (this.props.schema.definitions.hasOwnProperty("$luckytokenslink")) {
            this.luckytokenslinkValidationSchema = generateValidation(
                createValidation(
                    this.props.schema.definitions.$luckytokenslink.properties,
                    this.props.schema.definitions.$luckytokenslink.required
                )
            );
        }

        if (this.fractionatedFormSet.current) {
            this.fractionatedFormSet.current.fireChange();
        }
        if (this.proportionalFormSet.current) {
            this.proportionalFormSet.current.fireChange();
        }
        if (this.normalFormSet.current) {
            this.normalFormSet.current.fireChange();
        }
        if (this.levelFormSet.current) {
            this.levelFormSet.current.fireChange();
        }
        if (this.easylinkFormSet.current) {
            this.easylinkFormSet.current.fireChange();
        }
        if (this.sapFormSet.current) {
            this.sapFormSet.current.fireChange();
        }
        if (this.multipotsFormSet.current) {
            this.multipotsFormSet.current.fireChange();
        }
        if (this.jumbolinkFormSet.current) {
            this.jumbolinkFormSet.current.fireChange();
        }
        if (this.reelstrikeFormSet.current) {
            this.reelstrikeFormSet.current.fireChange();
        }
        if (this.luckytokenslinkFormSet.current) {
            this.luckytokenslinkFormSet.current.fireChange();
        }
    }

    onDialogClose = () => {
        this.setState(defaultState);
    };

    onDeletePrize = (deleteCallback, index) => {
        this.setState({
            showDialog: true,
            indexToDelete: index,
            deleteCallback
        });
    };

    onDialogConfirm = () => {
        this.state.deleteCallback(this.state.indexToDelete);
        this.setState(defaultState);
    };

    shouldComponentUpdate(nextProps, _nextState) {
        return (
            this.props.progressivesConfig.length !==
            nextProps.progressivesConfig.length ||
            !_.isEqual(
                this.props.selectedGameGroups,
                nextProps.selectedGameGroups
            ) ||
            !_.isEqual(
                this.props.selectedTerminalGroups,
                nextProps.selectedTerminalGroups
            ) ||
            !_.isEqual(this.state.showDialog, _nextState.showDialog)
        );
    }

    render() {
        if (
            this.props.editingConfiguration.settings.settings.settings !==
            null &&
            this.props.editingConfiguration.settings.settings.settings
                .game_types.length == 0
        ) {
            return (
                <div id="ProgressivesConfigurationSection">
                    <div className="row">
                        <div className="empty-prize-types">
                            <p>
                                <i className="icon-exclamation-circle-light"></i>
                                A game type must be selected in General Settings
                                to be able to add Progressives.
                            </p>
                        </div>
                    </div>
                </div>
            );
        }

        const fractionatedConfig = this.props.progressivesConfig.filter(
            progressive => progressive.type == "fractionated"
        );

        const proportionalConfig = this.props.progressivesConfig.filter(
            progressive => progressive.type == "proportional"
        );

        const normalConfig = this.props.progressivesConfig.filter(
            progressive => progressive.type == "normal"
        );

        const levelConfig = this.props.progressivesConfig.filter(
            progressive => progressive.type == "levels"
        );

        const multipotsConfig = this.props.progressivesConfig.filter(
            progressive => progressive.type == "multipots"
        );

        const jumbolinkConfig = this.props.progressivesConfig.filter(
            progressive => progressive.type == "jumbolink"
        );

        const reelstrikeConfig = this.props.progressivesConfig.filter(
            progressive => progressive.type == "reelstrike"
        );

        const easylinkConfig = this.props.progressivesConfig.filter(
            progressive => progressive.type == "easylink"
        );

        const sapConfig = this.props.progressivesConfig.filter(
            progressive => progressive.type == "sap"
        );

        const luckytokenslinkConfig = this.props.progressivesConfig.filter(
            progressive => progressive.type == "luckytokenslink"
        );

        const levelsFormTypes = waysSubforms(this.props)({
            terminalGroups: this.props.terminalGroups,
            gameGroups: this.props.gameGroups,
            selectedTerminalGroups: this.props.selectedTerminalGroups,
            selectedGameGroups: this.props.selectedGameGroups,
            schema: this.props.schema,
            selectedGroupType: this.props.selectedGroupType,
            progressivesConfig: this.props.progressivesConfig,
            type: "levels",
            rules: this.props.progressiveRules["levels"]
        });

        const multipotsFormTypes = waysSubforms(this.props)({
            terminalGroups: this.props.terminalGroups,
            gameGroups: this.props.gameGroups,
            selectedTerminalGroups: this.props.selectedTerminalGroups,
            selectedGameGroups: this.props.selectedGameGroups,
            schema: this.props.schema,
            selectedGroupType: this.props.selectedGroupType,
            progressivesConfig: this.props.progressivesConfig,
            type: "multipots",
            rules: this.props.progressiveRules["multipots"],
            subsidiary: this.props.editingConfiguration.subsidiary.country.code
        });

        const easylinkFormTypes = waysSubforms(this.props)({
            terminalGroups: this.props.terminalGroups,
            gameGroups: this.props.gameGroups,
            selectedTerminalGroups: this.props.selectedTerminalGroups,
            selectedGameGroups: this.props.selectedGameGroups,
            schema: this.props.schema,
            selectedGroupType: this.props.selectedGroupType,
            progressivesConfig: this.props.progressivesConfig,
            type: "easylink",
            rules: this.props.progressiveRules["easylink"],
            gameInstances: this.props.gameInstances
        });

        const hasSAP =
            this.props.schema.definitions.hasOwnProperty("$sap_jackpot");
        const hasReelstrike =
            this.props.schema.definitions.hasOwnProperty("$reelstrike");
        const hasJumbolink =
            this.props.schema.definitions.hasOwnProperty("$jumbolink");
        const hasLuckytokenslink =
            this.props.schema.definitions.hasOwnProperty("$luckytokenslink");

        let sapFormTypes = undefined;
        if (hasSAP) {
            sapFormTypes = waysSubforms(this.props)({
                terminalGroups: this.props.terminalGroups,
                gameGroups: this.props.gameGroups,
                selectedTerminalGroups: this.props.selectedTerminalGroups,
                selectedGameGroups: this.props.selectedGameGroups,
                schema: this.props.schema,
                selectedGroupType: this.props.selectedGroupType,
                progressivesConfig: this.props.progressivesConfig,
                type: "sap",
                rules: this.props.progressiveRules["sap"]
            });
        }

        let jumbolinkFormTypes = undefined;

        let reelstrikeFormTypes = undefined;
        let luckytokenslinkFormTypes = undefined;

        if (hasJumbolink) {
            jumbolinkFormTypes = waysSubforms(this.props)({
                terminalGroups: this.props.terminalGroups,
                gameGroups: this.props.gameGroups,
                selectedTerminalGroups: this.props.selectedTerminalGroups,
                selectedGameGroups: this.props.selectedGameGroups,
                schema: this.props.schema,
                selectedGroupType: this.props.selectedGroupType,
                progressivesConfig: this.props.progressivesConfig,
                type: "jumbolink",
                rules: this.props.progressiveRules["jumbolink"],
                subsidiary: this.props.editingConfiguration.subsidiary.country.code
            });
        }

        if (hasReelstrike) {
            reelstrikeFormTypes = waysSubforms(this.props)({
                terminalGroups: this.props.terminalGroups,
                gameGroups: this.props.gameGroups,
                selectedTerminalGroups: this.props.selectedTerminalGroups,
                selectedGameGroups: this.props.selectedGameGroups,
                schema: this.props.schema,
                selectedGroupType: this.props.selectedGroupType,
                progressivesConfig: this.props.progressivesConfig,
                type: "reelstrike",
                rules: this.props.progressiveRules["reelstrike"],
                subsidiary: this.props.editingConfiguration.subsidiary.country.code
            });
        }

        if (hasLuckytokenslink) {
            luckytokenslinkFormTypes = waysSubforms(this.props)({
                terminalGroups: this.props.terminalGroups,
                gameGroups: this.props.gameGroups,
                selectedTerminalGroups: this.props.selectedTerminalGroups,
                selectedGameGroups: this.props.selectedGameGroups,
                schema: this.props.schema,
                selectedGroupType: this.props.selectedGroupType,
                progressivesConfig: this.props.progressivesConfig,
                type: "luckytokenslink",
                rules: this.props.progressiveRules["luckytokenslink"],
                subsidiary: this.props.editingConfiguration.subsidiary.country.code,
                gameInstances: this.props.gameInstances
            });
        }

        const prizeSubForm = createPrizesSubForm(
            this.props.schema,
            this.props.selectedGroupType,
            {
                ...this.props.progressiveRules["easylink"],
                presets: this.props.progressiveRules["easylink"].presets
            },
            CONFIG_CONSTANTS.PROGRESSIVE_CHILDREN_TYPES,
            CONFIG_CONSTANTS.PROGRESSIVE_CHILDREN_TYPES,
            "$progressive",
            true
        );

        const enabledPrizeExtraFields = _.cloneDeep(prizeSubForm.extraFields);

        return (
            <div id="ProgressivesConfigurationSection">

                {this.props.schema.definitions.hasOwnProperty("$fractionated_jackpot") && (
                    <div className="col-12" id="fractionated-container">
                        <div className="inner-card">
                            <div className="inner-card-title">
                                <p>
                                    Fractionated
                                    <span>{` (${fractionatedConfig.length})`}</span>
                                </p>
                                <button
                                    onClick={() => {
                                        this.fractionatedFormSet.current.onAdd(0);
                                    }}
                                >
                                    <i className="icon-plus-light"></i>
                                </button>
                            </div>
                            <div className="inner-card-content">
                                <FormSetWays
                                    subsidiary={this.props.editingConfiguration.subsidiary.country.code}
                                    subFormTypes={fractionatedSubforms(this.props)({
                                        terminalGroups: this.props.terminalGroups,
                                        gameGroups: this.props.gameGroups,
                                        selectedTerminalGroups: this.props.selectedTerminalGroups,
                                        selectedGameGroups: this.props.selectedGameGroups,
                                        schema: this.props.schema,
                                        selectedGroupType: this.props.selectedGroupType,
                                        progressivesConfig: this.props.progressivesConfig,
                                        rules: this.props.progressiveRules["fractionated"]
                                    })}
                                    initialValues={fractionatedConfig}
                                    ref={this.fractionatedFormSet}
                                    onChange={this.onFractionatedsChange}
                                    hideAdd={true}
                                    onDelete={this.onDeletePrize}
                                    isConfig={true}
                                    presets={this.props.progressiveRules["fractionated"]}
                                />
                            </div>
                        </div>
                    </div>)}

                <div className="col-12" id="proportional-container">
                    <div className="inner-card">
                        <div className="inner-card-title">
                            <p>
                                Proportional
                                <span>{` (${proportionalConfig.length})`}</span>
                            </p>
                            <button
                                onClick={() => {
                                    this.proportionalFormSet.current.onAdd(0);
                                }}
                            >
                                <i className="icon-plus-light"></i>
                            </button>
                        </div>
                        <div className="inner-card-content">
                            <FormSet
                                subFormTypes={proportionalSubforms(this.props)({
                                    terminalGroups: this.props.terminalGroups,
                                    gameGroups: this.props.gameGroups,
                                    selectedTerminalGroups: this.props.selectedTerminalGroups,
                                    selectedGameGroups: this.props.selectedGameGroups,
                                    schema: this.props.schema,
                                    selectedGroupType: this.props.selectedGroupType,
                                    progressivesConfig: this.props.progressivesConfig
                                })}
                                initialValues={proportionalConfig}
                                ref={this.proportionalFormSet}
                                onChange={this.onProportionalsChange}
                                hideAdd={true}
                                onDelete={this.onDeletePrize}
                                isConfig={true}
                            />
                        </div>
                    </div>
                </div>

                <div className="col-12" id="normal-container">
                    <div className="inner-card">
                        <div className="inner-card-title">
                            <p>
                                Normal
                                <span>{` (${normalConfig.length})`}</span>
                            </p>
                            <button
                                onClick={() => {
                                    this.normalFormSet.current.onAdd(0);
                                }}
                            >
                                <i className="icon-plus-light"></i>
                            </button>
                        </div>
                        <div className="inner-card-content">
                            <FormSet
                                subFormTypes={normalSubforms(this.props)({
                                    terminalGroups: this.props.terminalGroups,
                                    gameGroups: this.props.gameGroups,
                                    selectedTerminalGroups: this.props.selectedTerminalGroups,
                                    selectedGameGroups: this.props.selectedGameGroups,
                                    schema: this.props.schema,
                                    selectedGroupType: this.props.selectedGroupType,
                                    progressivesConfig: this.props.progressivesConfig
                                })}
                                initialValues={normalConfig}
                                ref={this.normalFormSet}
                                onChange={this.onNormalsChange}
                                hideAdd={true}
                                onDelete={this.onDeletePrize}
                                isConfig={true}
                            />
                        </div>
                    </div>
                </div>

                <div className="col-12" id="levels-container">
                    <div className="inner-card">
                        <div className="inner-card-title">
                            <p>
                                Levels
                                <span>{` (${levelConfig.length})`}</span>
                            </p>
                            <button
                                onClick={() => {
                                    this.levelFormSet.current.onAdd(0);
                                }}
                            >
                                <i className="icon-plus-light"></i>
                            </button>
                        </div>
                        <div className="inner-card-content">
                            <FormSetWays
                                subFormTypes={levelsFormTypes}
                                initialValues={levelConfig}
                                ref={this.levelFormSet}
                                onChange={this.onLevelsChange}
                                hideAdd={true}
                                onDelete={this.onDeletePrize}
                                presets={this.props.progressiveRules["levels"]}
                                extraFields={enabledPrizeExtraFields}
                                isConfig={true}
                            />
                        </div>
                    </div>
                </div>

                <div className="col-12" id="multipots-container">
                    <div className="inner-card">
                        <div className="inner-card-title">
                            <p>
                                Multipots
                                <span>{` (${multipotsConfig.length})`}</span>
                            </p>
                            <button
                                onClick={() => {
                                    this.multipotsFormSet.current.onAdd(0);
                                }}
                            >
                                <i className="icon-plus-light"></i>
                            </button>
                        </div>
                        <div className="inner-card-content">
                            <FormSetWays
                                subFormTypes={multipotsFormTypes}
                                initialValues={multipotsConfig}
                                ref={this.multipotsFormSet}
                                onChange={this.onMultipotsChange}
                                hideAdd={true}
                                onDelete={this.onDeletePrize}
                                presets={
                                    this.props.progressiveRules["multipots"]
                                }
                                extraFields={enabledPrizeExtraFields}
                                isConfig={true}
                            />
                        </div>
                    </div>
                </div>

                <div className="col-12" id="easylink-container">
                    <div className="inner-card">
                        <div className="inner-card-title">
                            <p>
                                EasyLink
                                <span>{` (${easylinkConfig.length})`}</span>
                            </p>
                            <button
                                onClick={() => {
                                    this.easylinkFormSet.current.onAdd(0);
                                }}
                            >
                                <i className="icon-plus-light"></i>
                            </button>
                        </div>
                        <div className="inner-card-content">
                            <FormSetWays
                                subFormTypes={easylinkFormTypes}
                                initialValues={easylinkConfig}
                                ref={this.easylinkFormSet}
                                onChange={this.onEasylinkChange}
                                hideAdd={true}
                                onDelete={this.onDeletePrize}
                                presets={
                                    this.props.progressiveRules["easylink"]
                                }
                                isConfig={true}
                                extraFields={prizeSubForm.extraFields}
                                gameInstances={this.props.gameInstances}
                            />
                        </div>
                    </div>
                </div>

                {hasSAP && (
                    <div className="col-12" id="sap-container">
                        <div className="inner-card">
                            <div className="inner-card-title">
                                <p>
                                    SAP
                                    <span>{` (${sapConfig.length})`}</span>
                                </p>
                                <button
                                    onClick={() => {
                                        this.sapFormSet.current.onAdd(0);
                                    }}
                                >
                                    <i className="icon-plus-light"></i>
                                </button>
                            </div>
                            <div className="inner-card-content">
                                <FormSetWays
                                    subFormTypes={sapFormTypes}
                                    initialValues={sapConfig}
                                    ref={this.sapFormSet}
                                    onChange={this.onSAPChange}
                                    hideAdd={true}
                                    onDelete={this.onDeletePrize}
                                    presets={this.props.progressiveRules["sap"]}
                                    extraFields={prizeSubForm.extraFields}
                                    isConfig={true}
                                />
                            </div>
                        </div>
                    </div>
                )}
                {hasReelstrike && (
                    <div className="col-12" id="reelstrike-container">
                        <div className="inner-card">
                            <div className="inner-card-title">
                                <p>
                                    Reel Strike
                                    <span>{` (${reelstrikeConfig.length})`}</span>
                                </p>
                                <button
                                    onClick={() => {
                                        this.reelstrikeFormSet.current.onAdd(0);
                                    }}
                                >
                                    <i className="icon-plus-light"></i>
                                </button>
                            </div>
                            <div className="inner-card-content">
                                <FormSetWays
                                    subFormTypes={reelstrikeFormTypes}
                                    initialValues={reelstrikeConfig}
                                    ref={this.reelstrikeFormSet}
                                    onChange={this.onReelstrikeChange}
                                    hideAdd={true}
                                    onDelete={this.onDeletePrize}
                                    presets={
                                        this.props.progressiveRules["reelstrike"]
                                    }
                                    extraFields={enabledPrizeExtraFields}
                                    isConfig={true}
                                />
                            </div>
                        </div>
                    </div>)}

                {hasJumbolink && (
                    <div className="col-12" id="jumbolink-container">
                        <div className="inner-card">
                            <div className="inner-card-title">
                                <p>
                                    Jumbo Link
                                    <span>{` (${jumbolinkConfig.length})`}</span>
                                </p>
                                <button
                                    onClick={() => {
                                        this.jumbolinkFormSet.current.onAdd(0);
                                    }}
                                >
                                    <i className="icon-plus-light"></i>
                                </button>
                            </div>
                            <div className="inner-card-content">
                                <FormSetWays
                                    subFormTypes={jumbolinkFormTypes}
                                    initialValues={jumbolinkConfig}
                                    ref={this.jumbolinkFormSet}
                                    onChange={this.onJumbolinkChange}
                                    hideAdd={true}
                                    onDelete={this.onDeletePrize}
                                    presets={
                                        this.props.progressiveRules["jumbolink"]
                                    }
                                    extraFields={enabledPrizeExtraFields}
                                    isConfig={true}
                                />
                            </div>
                        </div>
                    </div>
                )}

                {hasLuckytokenslink && (
                    <div className="col-12" id="luckytokenslink-container">
                        <div className="inner-card">
                            <div className="inner-card-title">
                                <p>
                                    LuckyToken
                                    <span>{` (${luckytokenslinkConfig.length})`}</span>
                                </p>
                                <button
                                    onClick={() => {
                                        this.luckytokenslinkFormSet.current.onAdd(0);
                                    }}
                                >
                                    <i className="icon-plus-light"></i>
                                </button>
                            </div>
                            <div className="inner-card-content">
                                <FormSetWays
                                    subFormTypes={luckytokenslinkFormTypes}
                                    initialValues={luckytokenslinkConfig}
                                    ref={this.luckytokenslinkFormSet}
                                    onChange={this.onLuckytokenslinkChange}
                                    hideAdd={true}
                                    onDelete={this.onDeletePrize}
                                    presets={
                                        this.props.progressiveRules["luckytokenslink"]
                                    }
                                    extraFields={enabledPrizeExtraFields}
                                    isConfig={true}
                                    gameInstances={this.props.gameInstances}
                                />
                            </div>
                        </div>
                    </div>
                )}

                <Dialog
                    open={this.state.showDialog}
                    onConfirm={this.onDialogConfirm}
                    onClose={this.onDialogClose}
                    invertedButtons
                >
                    You are about to delete a Progressive configuration. Are you
                    sure?
                </Dialog>
            </div>
        );
    }
}

const mapStateToProps = state => ({
    progressivesConfig: configurationsStoreSelectors.progressivesConfig(state),
    terminalGroups: configurationsStoreSelectors.terminalGroups(state),
    selectedTerminalGroups: configurationsStoreSelectors.selectedTerminalGroups(state),
    selectedGameGroups: configurationsStoreSelectors.selectedGameGroups(state),
    schema: configurationsStoreSelectors.progressivesSchema(state),
    denominationsList: configurationsStoreSelectors.denominationsList(state),
    selectedGroupType: configurationsStoreSelectors.selectedGroupType(state),
    gameGroups: configurationsStoreSelectors.gameGroups(state),
    progressiveRules: configurationsStoreSelectors.progressiveRules(state),
    editingConfiguration: configurationsStoreSelectors.editingConfiguration(state),
    gameInstances: configurationsStoreSelectors.gameInstances(state),
});

const mapDispatchToProps = {
    updateProgressivesConfig:
    ConfigurationsActionCreators.updateProgressivesConfig,
    configSectionVisited: ConfigurationsActionCreators.configSectionVisited,
    getGameInstances: ConfigurationsActionCreators.getGameInstances,
};

export default connect(
    mapStateToProps,
    mapDispatchToProps
)(ProgressivesConfigurationSection);
